import {
  escapeHTML,
  track,
  debounce,
  registerServiceWorker,
  placeholderImage,
  trackMediaLoad,
  mediaAltText,
  mailtoLink,
} from './common.js';

const body = document.body;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const pageParams = new URLSearchParams(location.search);
const MODE_STORAGE_KEY = 'nero_mode_v1';

function storedMode() {
  try {
    const saved = localStorage.getItem(MODE_STORAGE_KEY);
    return saved === 'business' || saved === 'creator' ? saved : '';
  } catch (_) {
    return '';
  }
}

// An explicit path or query wins; otherwise fall back to the visitor's last
// chosen mode so the switch actually persists across pages.
const initialMode = location.pathname.startsWith('/for-brands') || pageParams.get('mode') === 'studio'
  ? 'business'
  : pageParams.get('mode') === 'creator'
    ? 'creator'
    : storedMode() || 'creator';
body.dataset.mode = initialMode;

document.querySelectorAll('.track-click').forEach((element) => {
  element.addEventListener('click', () => track(element.dataset.track || 'unlabelled_click'));
});

// The boot overlay used to run a fake progress counter for well over a second
// before the page was revealed, which is pure invented latency in front of first
// paint. It now tracks real readiness and is capped hard.
const boot = document.querySelector('#boot');
const bootProgress = document.querySelector('#boot-progress');
const bootValue = document.querySelector('#boot-value');
const BOOT_MAX_MS = reducedMotion ? 0 : 320;
let bootDone = false;
let bootCount = 0;

function paintBoot(value) {
  bootCount = Math.max(bootCount, Math.min(100, Math.round(value)));
  if (bootProgress) bootProgress.style.width = `${bootCount}%`;
  if (bootValue) bootValue.textContent = String(bootCount).padStart(2, '0');
}

function finishBoot() {
  if (bootDone || !boot) return;
  bootDone = true;
  paintBoot(100);
  if (reducedMotion) {
    boot.remove();
    return;
  }

  // Keep the launch screen tied to real readiness, but let the exit motion
  // actually finish. The panel transition is 850ms; removing it after 260ms
  // made it disappear halfway through the slide.
  const removeBoot = () => boot.remove();
  boot.addEventListener('transitionend', (event) => {
    if (event.target === boot && event.propertyName === 'transform') removeBoot();
  }, { once: true });
  requestAnimationFrame(() => boot.classList.add('is-done'));
  setTimeout(removeBoot, 1100);
}

if (boot) {
  const bootStart = performance.now();
  const bootTick = () => {
    if (bootDone) return;
    const elapsed = performance.now() - bootStart;
    paintBoot(Math.min(96, (elapsed / Math.max(1, BOOT_MAX_MS)) * 100));
    if (elapsed >= BOOT_MAX_MS) finishBoot();
    else requestAnimationFrame(bootTick);
  };
  requestAnimationFrame(bootTick);
  // Never outlive the cap, whatever else is still in flight.
  setTimeout(finishBoot, BOOT_MAX_MS + 60);
}

const menu = document.querySelector('#menu-overlay');
const menuTrigger = document.querySelector('[data-menu-trigger]');
function toggleMenu(force) {
  const open = force ?? !menu.classList.contains('is-open');
  const wasOpen = menu.classList.contains('is-open');
  menu.classList.toggle('is-open', open);
  menu.setAttribute('aria-hidden', String(!open));
  menuTrigger.setAttribute('aria-expanded', String(open));
  body.classList.toggle('is-locked', open);
  // Move focus with the overlay so keyboard and screen-reader users are not
  // left behind the panel, and hand it back to the trigger on close.
  if (open && !wasOpen) {
    menu.querySelector('a, button')?.focus({ preventScroll: true });
  } else if (!open && wasOpen) {
    menuTrigger.focus({ preventScroll: true });
  }
  requestAnimationFrame(() => typeof syncHeaderContrast === 'function' && syncHeaderContrast());
}
menuTrigger.addEventListener('click', () => toggleMenu());
document.querySelectorAll('[data-menu-link]').forEach((link) => link.addEventListener('click', () => toggleMenu(false)));
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !menu.classList.contains('is-open')) return;
  toggleMenu(false);
});
// Keep tab focus inside the overlay while it is the active surface.
menu.addEventListener('keydown', (event) => {
  if (event.key !== 'Tab' || !menu.classList.contains('is-open')) return;
  const focusable = [...menu.querySelectorAll('a[href], button:not([disabled])')]
    .filter((node) => node.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

function applyMode(mode, { updateUrl = false, scrollToMode = false, record = false } = {}) {
  body.dataset.mode = mode;
  document.querySelectorAll('[data-mode-button]').forEach((item) => {
    const active = item.dataset.modeButton === mode;
    item.classList.toggle('is-active', active);
    item.setAttribute('aria-pressed', String(active));
  });
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch (_) { /* Persistence is a nicety, not a requirement. */ }
  if (updateUrl) {
    const url = new URL(location.href);
    if (mode === 'business') url.searchParams.set('mode', 'studio');
    else url.searchParams.delete('mode');
    history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }
  if (record) track('mode_switch', { mode });
  if (scrollToMode) window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
}
applyMode(initialMode);
document.querySelectorAll('[data-mode-button]').forEach((button) => {
  button.addEventListener('click', () => {
    applyMode(button.dataset.modeButton, { updateUrl: true, scrollToMode: true, record: true });
  });
});

const heroSection = document.querySelector('#intro');
const heroModeObserver = new IntersectionObserver(([entry]) => {
  body.classList.toggle('hero-in-view', entry.isIntersecting);
}, { threshold: .18 });
heroModeObserver.observe(heroSection);

const lightHeaderSections = [...document.querySelectorAll('[data-header-theme="light"]')];
function syncHeaderContrast() {
  const headerY = Math.min(42, innerHeight * .08);
  const lightSection = lightHeaderSections.some((section) => {
    if (section.hidden) return false;
    const bounds = section.getBoundingClientRect();
    return bounds.top <= headerY && bounds.bottom > headerY;
  });
  const lightHero = body.dataset.mode === 'business' && (() => {
    const bounds = heroSection.getBoundingClientRect();
    return bounds.top <= headerY && bounds.bottom > headerY;
  })();
  body.classList.toggle('header-on-light', !menu.classList.contains('is-open') && (lightSection || lightHero));
}
addEventListener('scroll', syncHeaderContrast, { passive: true });
addEventListener('resize', syncHeaderContrast, { passive: true });
syncHeaderContrast();

if (!reducedMotion) {
  const resetHeroMotion = () => {
    heroSection.style.setProperty('--hero-x', '0px');
    heroSection.style.setProperty('--hero-y', '0px');
    heroSection.style.setProperty('--hero-ox', '0px');
    heroSection.style.setProperty('--hero-oy', '0px');
  };
  heroSection.addEventListener('pointermove', (event) => {
    const bounds = heroSection.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - .5;
    const y = (event.clientY - bounds.top) / bounds.height - .5;
    heroSection.style.setProperty('--hero-x', `${x * 10}px`);
    heroSection.style.setProperty('--hero-y', `${y * 8}px`);
    heroSection.style.setProperty('--hero-ox', `${x * -7}px`);
    heroSection.style.setProperty('--hero-oy', `${y * -5}px`);
  });
  heroSection.addEventListener('pointerleave', resetHeroMotion);
  heroSection.addEventListener('pointerup', (event) => {
    if (event.pointerType === 'touch') resetHeroMotion();
  });
  heroSection.addEventListener('pointercancel', resetHeroMotion);
}

// A shared liquid-glass highlight follows the pointer inside interactive surfaces.
const liquidSelectors = [
  '.mode-switch',
  '.liquid-button',
  '.studio-console',
  '.ai-search > div',
  '.download-card',
  '.contact-form',
  '.newsletter form',
  '.portal'
];
document.querySelectorAll(liquidSelectors.join(',')).forEach((surface) => {
  surface.classList.add('liquid-surface');
  surface.addEventListener('pointermove', (event) => {
    const bounds = surface.getBoundingClientRect();
    surface.style.setProperty('--glass-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
    surface.style.setProperty('--glass-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
  });
  surface.addEventListener('pointerleave', () => {
    surface.style.setProperty('--glass-x', '-20%');
    surface.style.setProperty('--glass-y', '-20%');
  });
});

function updateClock() {
  document.querySelector('#local-time').textContent = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Lisbon', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).format(new Date());
}
updateClock();
setInterval(updateClock, 1000);

const scrollProgress = document.querySelector('#scroll-progress');
window.addEventListener('scroll', () => {
  const max = document.documentElement.scrollHeight - innerHeight;
  scrollProgress.style.width = `${max ? (scrollY / max) * 100 : 0}%`;
}, { passive: true });

const hatDrop = document.querySelector('#hat-drop');
const hatCanvas = document.querySelector('[data-hat-points]');
const hatStage = document.querySelector('[data-hat-stage]');
const hatFlight = document.querySelector('[data-hat-flight]');
const hatOrigin = document.querySelector('[data-hat-origin]');
const hatLanding = document.querySelector('[data-hat-landing]');
const hero = document.querySelector('#intro');
const hatDesktopMedia = window.matchMedia('(min-width: 781px) and (hover: hover) and (pointer: fine)');
body.classList.toggle('hat-disabled-mobile', !hatDesktopMedia.matches);
if (hatDesktopMedia.matches && hatDrop && hatCanvas && hatStage && hatFlight && hatOrigin && hatLanding && hero) {
  let hatPointCloud = null;
  let hatLoading = false;
  const mountHat = async () => {
    if (hatLoading || hatPointCloud) return;
    hatLoading = true;
    try {
      const { mountHatPointCloud } = await import('./hat-points.js');
      hatPointCloud = await mountHatPointCloud(
        hatCanvas,
        new URL('./assets/hat/bucket_hat.glb', import.meta.url).href,
      );
      updateHatProgress();
    } catch (_) {
      hatDrop.classList.add('hat-render-failed');
    } finally {
      hatLoading = false;
    }
  };
  const hatLoadObserver = new IntersectionObserver(([entry], observer) => {
    if (!entry.isIntersecting) return;
    mountHat();
    observer.disconnect();
  }, { rootMargin: '40% 0px' });
  hatLoadObserver.observe(hero);

  let hatFrame = 0;
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
  const mix = (start, end, progress) => start + (end - start) * progress;
  const easeJourney = (progress) => {
    const t = clamp(progress);
    return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  function updateHatProgress() {
    const dropBounds = hatDrop.getBoundingClientRect();
    const heroBounds = hero.getBoundingClientRect();
    const originBounds = hatOrigin.getBoundingClientRect();
    const landingBounds = hatLanding.getBoundingClientRect();
    const travel = innerHeight + dropBounds.height;
    const progress = clamp((innerHeight - dropBounds.top) / travel);
    const entry = clamp((innerHeight - dropBounds.top) / (innerHeight * .72));
    const easedEntry = 1 - Math.pow(1 - entry, 3);
    const journeyStart = hero.offsetTop + Math.max(innerHeight * .12, hero.offsetHeight * .08);
    const journeyEnd = Math.max(journeyStart + innerHeight * .55, hatDrop.offsetTop + innerHeight * .2);
    const journey = easeJourney((scrollY - journeyStart) / Math.max(1, journeyEnd - journeyStart));
    const originX = originBounds.left + originBounds.width / 2;
    const originY = originBounds.top + originBounds.height / 2;
    const landingX = landingBounds.left + landingBounds.width / 2;
    const landingY = landingBounds.top + landingBounds.height / 2;
    const sourceWidth = Math.max(48, originBounds.width);
    const sourceHeight = Math.max(38, originBounds.height);
    const viewportGutter = innerWidth <= 600 ? 12 : 0;
    const landingRatio = landingBounds.width / Math.max(1, landingBounds.height);
    const availableWidth = Math.max(sourceWidth, innerWidth - viewportGutter * 2);
    const targetWidth = Math.max(sourceWidth, Math.min(landingBounds.width, availableWidth));
    const targetHeight = Math.max(
      sourceHeight,
      Math.min(landingBounds.height, innerWidth <= 600 ? targetWidth / Math.max(.65, landingRatio) : landingBounds.height)
    );
    const width = mix(sourceWidth, targetWidth, journey);
    const height = mix(sourceHeight, targetHeight, journey);
    const rawX = mix(originX, landingX, journey) - width / 2;
    const x = innerWidth <= 600
      ? clamp(rawX, viewportGutter, Math.max(viewportGutter, innerWidth - width - viewportGutter))
      : rawX;
    const y = mix(originY, landingY, journey) - height / 2;
    const creatorMode = document.body.dataset.mode !== 'business';
    const visible = creatorMode
      && heroBounds.top < innerHeight
      && dropBounds.top < innerHeight * 1.4
      && dropBounds.bottom > -innerHeight * .35;

    hatFlight.style.width = `${Math.max(1, width)}px`;
    hatFlight.style.height = `${Math.max(1, height)}px`;
    hatFlight.style.transform = `translate3d(${x}px,${y}px,0)`;
    hatFlight.style.setProperty('--hat-journey', journey.toFixed(3));
    hatFlight.classList.toggle('is-visible', visible);
    hatFlight.classList.toggle('is-landed', journey > .965);
    hatDrop.style.setProperty('--hat-progress', progress.toFixed(3));
    hatDrop.style.setProperty('--hat-entry', easedEntry.toFixed(3));
    hatPointCloud?.setProgress(progress, Math.max(.3, journey * .7 + easedEntry * .3));
    hatPointCloud?.setActive(visible && !document.hidden);
    hatFrame = 0;
  }

  function queueHatUpdate() {
    if (!hatFrame) hatFrame = requestAnimationFrame(updateHatProgress);
  }
  window.addEventListener('scroll', () => {
    queueHatUpdate();
  }, { passive: true });
  window.addEventListener('resize', queueHatUpdate, { passive: true });
  document.addEventListener('visibilitychange', queueHatUpdate);
  new MutationObserver(queueHatUpdate).observe(document.body, { attributes: true, attributeFilter: ['data-mode'] });
  updateHatProgress();
}

const revealObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => entry.target.classList.toggle('is-visible', entry.isIntersecting));
}, { threshold: .12 });
document.querySelectorAll('.reveal').forEach((element) => revealObserver.observe(element));

// An original concentric radio-wave field rendered as pure text.
const sphereNode = document.querySelector('#ascii-sphere');
const radioChars = ' .·,:;+=xX$#@';
let radioPhase = 0;
function renderSphere() {
  if (!sphereNode) return;
  const compact = innerWidth < 600;
  const width = compact ? 70 : 96;
  const height = compact ? 62 : 78;
  const centerX = Math.cos(radioPhase * .31) * .09;
  const centerY = Math.sin(radioPhase * .23) * .075;
  const rows = [];

  for (let row = 0; row < height; row += 1) {
    let line = '';
    for (let column = 0; column < width; column += 1) {
      const x = ((column + .5) / width) * 2 - 1;
      const y = ((row + .5) / height) * 2 - 1;
      const stageRadius = Math.hypot(x, y);
      if (stageRadius > .985) {
        line += ' ';
        continue;
      }

      const dx = x - centerX;
      const dy = y - centerY;
      const distance = Math.hypot(dx, dy);
      const angle = Math.atan2(dy, dx);
      const ripple = Math.cos(distance * 43 - radioPhase * 4.3 + Math.sin(angle * 3 + radioPhase) * .38);
      const echo = Math.cos(distance * 22 + radioPhase * 1.7 - angle * .65) * .24;
      const signal = Math.max(0, ripple * .74 + echo + .22);
      const falloff = .58 + (1 - Math.min(distance, 1)) * .42;
      const shimmer = Math.sin(column * 12.9898 + row * 78.233 + radioPhase * 2.1) * .055;
      const level = Math.min(1, Math.pow(Math.max(0, signal * falloff + shimmer), 1.28));
      const charIndex = Math.min(radioChars.length - 1, Math.floor(level * radioChars.length));
      line += distance < .032 ? '●' : radioChars[charIndex];
    }
    rows.push(line);
  }

  sphereNode.textContent = rows.join('\n');
  radioPhase += .045;
}
renderSphere();
if (!reducedMotion) setInterval(renderSphere, 58);

// Reactive background dots move away from the pointer, then ease home.
const dotCanvas = document.querySelector('#dot-field');
const dotCtx = dotCanvas.getContext('2d');
let dots = [];
const pointer = { x: -1000, y: -1000 };
function sizeDots() {
  const ratio = Math.min(devicePixelRatio, 2);
  dotCanvas.width = innerWidth * ratio;
  dotCanvas.height = innerHeight * ratio;
  dotCanvas.style.width = `${innerWidth}px`;
  dotCanvas.style.height = `${innerHeight}px`;
  dotCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
  const gap = innerWidth < 700 ? 42 : 54;
  dots = [];
  for (let y = gap / 2; y < innerHeight; y += gap) {
    for (let x = gap / 2; x < innerWidth; x += gap) dots.push({ x, y, ox: x, oy: y, vx: 0, vy: 0 });
  }
}
function drawDots() {
  dotCtx.clearRect(0, 0, innerWidth, innerHeight);
  dots.forEach((dot) => {
    const dx = dot.x - pointer.x;
    const dy = dot.y - pointer.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 145) {
      const force = (145 - distance) / 145;
      dot.vx += (dx / (distance || 1)) * force * 1.7;
      dot.vy += (dy / (distance || 1)) * force * 1.7;
    }
    dot.vx += (dot.ox - dot.x) * .018;
    dot.vy += (dot.oy - dot.y) * .018;
    dot.vx *= .89; dot.vy *= .89;
    dot.x += dot.vx; dot.y += dot.vy;
    dotCtx.beginPath();
    dotCtx.arc(dot.x, dot.y, distance < 145 ? 1.8 : 1.05, 0, Math.PI * 2);
    dotCtx.fillStyle = distance < 145 ? '#ffcd44' : 'rgba(241,238,230,.55)';
    dotCtx.fill();
  });
  if (!reducedMotion) requestAnimationFrame(drawDots);
}
function resetReactivePointer() {
  pointer.x = -1000;
  pointer.y = -1000;
}
window.addEventListener('pointermove', (event) => {
  pointer.x = event.clientX;
  pointer.y = event.clientY;
  if (event.pointerType === 'touch') {
    body.classList.add('is-touch');
    window.clearTimeout(resetReactivePointer.timer);
    resetReactivePointer.timer = window.setTimeout(resetReactivePointer, 90);
  }
});
window.addEventListener('pointerup', (event) => {
  if (event.pointerType === 'touch') resetReactivePointer();
});
window.addEventListener('pointercancel', resetReactivePointer);
window.addEventListener('pointerleave', resetReactivePointer);
window.addEventListener('resize', sizeDots);
sizeDots(); drawDots();

function contentAt(object, path) {
  return path.split('.').reduce((value, key) => value?.[key], object);
}

const SECTION_TARGETS = {
  hero: '#intro',
  hatDrop: '#hat-drop',
  work: '#work',
  about: '#about',
  services: '#services',
  examples: '#examples',
  proof: '#proof',
  vault: '#vault',
  mediaKit: '#media-kit',
  contact: '#contact',
  footer: '.site-footer',
};

function applySectionVisibility(sections = {}) {
  Object.entries(SECTION_TARGETS).forEach(([key, selector]) => {
    const section = document.querySelector(selector);
    if (section) section.hidden = sections[key] === false;
  });

  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    const target = link.getAttribute('href')?.slice(1);
    const match = Object.entries(SECTION_TARGETS).find(([, selector]) => selector === `#${target}`);
    if (!match || !link.matches('[data-menu-link], .hero__actions a, .hero__scroll, .text-arrow')) return;
    link.hidden = sections[match[0]] === false;
  });
}

// Single source of truth for the published contact address; overwritten by
// /api/site-content so no template has to hardcode it.
let contactEmail = 'contact@nerosoares.com';
let archiveAlbums = [];
let archiveCollections = [];
const archivePreviewLimit = () => matchMedia('(max-width: 640px)').matches ? 4 : 3;

function syncMediaAspect(media, frame, { lockFrame = true } = {}) {
  const apply = () => {
    const width = media.naturalWidth || media.videoWidth;
    const height = media.naturalHeight || media.videoHeight;
    if (!width || !height) return;
    media.dataset.aspectRatio = String(width / height);
    // Only the first frame sizes its container. Letting every slide rewrite the
    // ratio made the whole card jump height on each carousel change.
    if (lockFrame && frame && !frame.dataset.aspectLocked) {
      frame.dataset.aspectLocked = '1';
      frame.style.aspectRatio = `${width} / ${height}`;
    }
  };
  trackMediaLoad(media);
  media.addEventListener(media.tagName === 'VIDEO' ? 'loadedmetadata' : 'load', apply, { once: true });
  if (media.complete || media.readyState >= 1) apply();
}

function createProjectSlide(item, title, index, frame) {
  const isVideo = item.kind === 'video';
  const node = document.createElement(isVideo ? 'video' : 'img');
  node.src = item.url;
  node.draggable = false;
  node.dataset.carouselSlide = '';
  if (isVideo) {
    node.controls = true;
    node.playsInline = true;
    node.preload = index ? 'metadata' : 'auto';
    node.setAttribute('aria-label', mediaAltText(item, title, index));
  } else {
    node.alt = mediaAltText(item, title, index);
    node.decoding = 'async';
    if (index) node.loading = 'lazy';
    else node.fetchPriority = 'high';
  }
  if (index) node.setAttribute('aria-hidden', 'true');
  else node.classList.add('is-active');
  syncMediaAspect(node, frame, { lockFrame: index === 0 });
  return node;
}

function renderArchiveAlbums(albums) {
  const grid = document.querySelector('#album-grid');
  if (!grid) return;
  grid.replaceChildren(...albums.map((album, index) => {
    const card = document.createElement('article');
    card.className = 'album-card';
    card.hidden = index >= archivePreviewLimit();
    card.dataset.keywords = [
      album.title,
      album.description,
      album.visibility,
      ...(album.media || []).map((item) => item.name),
    ].filter(Boolean).join(' ').toLowerCase();

    const open = document.createElement('button');
    open.type = 'button';
    open.dataset.openAlbum = album.id;
    const cover = album.media?.[0];
    if (cover) {
      const media = document.createElement(cover.kind === 'video' ? 'video' : 'img');
      media.src = cover.url;
      if (cover.kind === 'video') {
        media.muted = true;
        media.playsInline = true;
        media.preload = 'metadata';
      } else {
        media.alt = `${album.title} album cover`;
        media.decoding = 'async';
        media.loading = index < archivePreviewLimit() ? 'eager' : 'lazy';
      }
      open.dataset.mediaFrame = '';
      syncMediaAspect(media, open);
      open.append(media);
    } else {
      const empty = document.createElement('span');
      empty.className = 'album-card__empty';
      empty.textContent = 'FIRST FRAME PENDING';
      open.append(empty);
    }
    const badge = document.createElement('span');
    badge.className = `album-card__badge${album.visibility === 'public' ? '' : ' album-card__badge--locked'}`;
    badge.textContent = album.visibility === 'public' ? 'Public' : 'Client access';
    open.append(badge);

    const copy = document.createElement('div');
    const title = document.createElement('h3');
    title.textContent = album.title;
    const meta = document.createElement('p');
    const count = album.media?.length || 0;
    const displayDate = album.albumDate
      ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        .format(new Date(`${album.albumDate}T00:00:00Z`)).toUpperCase()
      : '';
    meta.textContent = `${count} FILE${count === 1 ? '' : 'S'}${displayDate ? ` · ${displayDate}` : ''}`;
    const share = document.createElement('button');
    share.type = 'button';
    share.className = 'icon-button';
    share.setAttribute('aria-label', `Share ${album.title}`);
    share.dataset.share = album.id;
    share.dataset.shareTitle = album.title;
    share.textContent = '↗';
    copy.append(title, meta, share);
    card.append(open, copy);
    return card;
  }));
  grid.removeAttribute('aria-busy');
  const empty = document.querySelector('#vault-empty');
  if (empty) empty.hidden = albums.length > 0;
}

function renderPublicCollections(collections, albums) {
  const section = document.querySelector('[data-public-collections]');
  const grid = document.querySelector('[data-collection-grid]');
  if (!section || !grid) return;
  const albumById = new Map(albums.map((album) => [album.id, album]));
  const visible = collections.map((collection) => ({
    ...collection,
    albums: (collection.albumIds || []).map((id) => albumById.get(id)).filter(Boolean),
  })).filter((collection) => collection.albums.length);
  section.hidden = visible.length === 0;
  document.querySelector('[data-collection-count]').textContent = String(visible.length).padStart(2, '0');
  grid.innerHTML = visible.map((collection) => {
    const cover = collection.coverUrl || collection.albums[0]?.media?.[0]?.url || '';
    return `<a class="collection-card" href="/archive?collection=${encodeURIComponent(collection.id)}">
      <span class="collection-card__media" data-media-frame>${cover
        ? `<img src="${escapeHTML(cover)}" alt="" loading="lazy" decoding="async" />`
        : `<img src="${placeholderImage()}" alt="" width="1200" height="800" />`}</span>
      <span class="collection-card__copy"><small>${collection.albums.length} ALBUM${collection.albums.length === 1 ? '' : 'S'}</small><h3>${escapeHTML(collection.title)}</h3></span>
    </a>`;
  }).join('');
  grid.querySelectorAll('img').forEach(trackMediaLoad);
}

function initAccessCarousel(albums, settings = {}) {
  const root = document.querySelector('[data-access-carousel]');
  const mediaRoot = document.querySelector('[data-access-carousel-media]');
  if (!root || !mediaRoot) return;
  if (settings.enabled === false) {
    root.hidden = true;
    return;
  }
  const selectedIds = Array.isArray(settings.albumIds) ? new Set(settings.albumIds) : new Set();
  const configuredAlbums = settings.mode === 'selected' && selectedIds.size
    ? albums.filter((album) => selectedIds.has(album.id))
    : albums;
  const sourceAlbums = configuredAlbums.length ? configuredAlbums : albums;
  const candidates = sourceAlbums.flatMap((album) => (album.media || [])
    .filter((media) => media.kind !== 'video' && media.url)
    .map((media) => ({ album, media })));
  const compact = window.matchMedia('(max-width: 720px)').matches;
  const targetCount = compact ? 3 : 5;
  // Enough variety to feel alive, without downloading the whole archive.
  const poolLimit = compact ? targetCount + 2 : targetCount + 4;
  const shuffled = settings.mode === 'selected' ? candidates : candidates.sort(() => Math.random() - .5);
  const pool = shuffled.slice(0, poolLimit);
  if (!pool.length) { root.hidden = true; return; }
  mediaRoot.replaceChildren();
  root.hidden = false;
  const preloaded = new Map();
  const active = new Map();
  const reservedUrls = new Set();
  const reservedAnchors = new Set();
  const anchors = compact ? [
    { x: 3, y: 7, w: 42 }, { x: 55, y: 20, w: 39 }, { x: 8, y: 51, w: 45 },
    { x: 52, y: 65, w: 42 }, { x: 30, y: 34, w: 38 },
  ] : [
    { x: 2, y: 7, w: 15 }, { x: 22, y: 57, w: 14 }, { x: 39, y: 11, w: 13 },
    { x: 58, y: 58, w: 16 }, { x: 82, y: 8, w: 14 }, { x: 4, y: 61, w: 17 },
    { x: 77, y: 50, w: 13 }, { x: 37, y: 64, w: 15 }, { x: 60, y: 20, w: 12 },
  ];

  const randomBetween = (min, max) => min + Math.random() * (max - min);
  const schedule = (callback, delay) => window.setTimeout(callback, delay);

  const preload = (item) => {
    if (!item?.media?.url) return Promise.resolve(false);
    if (preloaded.has(item.media.url)) return preloaded.get(item.media.url);
    const pending = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = async () => {
        try { await image.decode(); } catch {}
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.src = item.media.url;
    });
    preloaded.set(item.media.url, pending);
    return pending;
  };

  const chooseItem = () => {
    const used = new Set([...active.values()].map((entry) => entry.url).concat([...reservedUrls]));
    const available = pool.filter((item) => !used.has(item.media.url));
    const source = available.length ? available : pool;
    return source[Math.floor(Math.random() * source.length)];
  };

  const chooseAnchor = () => {
    const used = new Set([...active.values()].map((entry) => entry.anchor).concat([...reservedAnchors]));
    const available = anchors.map((anchor, index) => ({ anchor, index })).filter(({ index }) => !used.has(index));
    return available[Math.floor(Math.random() * available.length)] || { anchor: anchors[0], index: 0 };
  };

  const armRetirement = (tile, delay = randomBetween(6800, 10800)) => {
    const entry = active.get(tile);
    if (!entry || reducedMotion) return;
    if (entry.retireTimer) window.clearTimeout(entry.retireTimer);
    entry.retireTimer = schedule(() => retire(tile), delay);
  };

  const retire = (tile) => {
    const entry = active.get(tile);
    if (!entry) return;
    if (entry.hovered) {
      armRetirement(tile, 1800);
      return;
    }
    if (entry.retireTimer) window.clearTimeout(entry.retireTimer);
    tile.classList.remove('is-visible');
    tile.classList.add('is-leaving');
    schedule(() => {
      active.delete(tile);
      tile.remove();
      if (!reducedMotion) schedule(spawn, randomBetween(450, 1300));
    }, 1250);
  };

  const spawn = async () => {
    if (active.size >= targetCount || document.hidden) return;
    const item = chooseItem();
    const placement = chooseAnchor();
    if (!item || !placement) return;
    reservedUrls.add(item.media.url);
    reservedAnchors.add(placement.index);
    await preload(item);
    reservedUrls.delete(item.media.url);
    reservedAnchors.delete(placement.index);
    if (active.size >= targetCount) return;

    const tile = document.createElement('article');
    const image = document.createElement('img');
    const link = document.createElement('a');
    const title = document.createElement('b');
    const arrow = document.createElement('i');
    tile.className = 'hero-access-carousel__tile';
    tile.style.setProperty('--tile-x', `${placement.anchor.x}%`);
    tile.style.setProperty('--tile-y', `${placement.anchor.y}%`);
    tile.style.setProperty('--tile-w', `${placement.anchor.w}vw`);
    tile.style.setProperty('--tile-r', `${randomBetween(-3.2, 3.2).toFixed(2)}deg`);
    tile.style.setProperty('--tile-drift-x', `${randomBetween(-1.2, 1.2).toFixed(2)}rem`);
    tile.style.setProperty('--tile-drift-y', `${randomBetween(-.8, .8).toFixed(2)}rem`);
    tile.style.setProperty('--tile-opacity', randomBetween(compact ? .18 : .22, compact ? .28 : .37).toFixed(2));
    image.src = item.media.url;
    image.alt = '';
    image.decoding = 'async';
    image.loading = 'lazy';
    image.draggable = false;
    image.setAttribute('aria-hidden', 'true');
    link.className = 'hero-access-carousel__meta';
    link.href = `/album?id=${encodeURIComponent(item.album.id)}`;
    link.setAttribute('aria-label', `Open ${item.album.title}`);
    title.textContent = item.album.title;
    arrow.textContent = '↗';
    arrow.setAttribute('aria-hidden', 'true');
    link.append(title, arrow);
    tile.append(image, link);
    mediaRoot.append(tile);
    active.set(tile, {
      url: item.media.url,
      anchor: placement.index,
      hovered: false,
      retireTimer: null,
    });
    tile.addEventListener('pointerenter', () => {
      const entry = active.get(tile);
      if (!entry) return;
      entry.hovered = true;
      if (entry.retireTimer) window.clearTimeout(entry.retireTimer);
      tile.classList.add('is-hovered');
    });
    tile.addEventListener('pointerleave', () => {
      const entry = active.get(tile);
      if (!entry) return;
      entry.hovered = false;
      tile.classList.remove('is-hovered');
      armRetirement(tile, randomBetween(1600, 2400));
    });
    requestAnimationFrame(() => requestAnimationFrame(() => tile.classList.add('is-visible')));
    armRetirement(tile);
  };

  pool.slice(0, Math.min(pool.length, targetCount + 2)).forEach(preload);
  for (let index = 0; index < Math.min(targetCount, pool.length); index += 1) {
    schedule(spawn, reducedMotion ? 0 : index * 520);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    for (let index = active.size; index < Math.min(targetCount, pool.length); index += 1) {
      schedule(spawn, (index - active.size) * 280);
    }
  }, { passive: true });
}

async function hydrateSiteContent() {
  try {
    const [response, albumsResponse] = await Promise.all([
      fetch('/api/site-content', { cache: 'no-store' }),
      fetch('/api/albums'),
    ]);
    if (!response.ok) return;
    const { content, media = {}, files = {} } = await response.json();
    if (!content) return;
    if (albumsResponse.ok) {
      const payload = await albumsResponse.json();
      archiveAlbums = (payload.albums || []).filter((album) => album.status === 'published');
      archiveCollections = payload.collections || [];
      renderArchiveAlbums(archiveAlbums);
      renderPublicCollections(archiveCollections, archiveAlbums);
      initAccessCarousel(archiveAlbums, {
        enabled: content.hero?.accessCarouselEnabled,
        mode: content.hero?.accessCarouselMode,
        albumIds: content.hero?.accessCarouselAlbumIds,
      });
      const search = document.querySelector('#archive-search');
      if (search) {
        const usefulExample = (value) => {
          const normalized = String(value || '')
            .replace(/\.[a-z0-9]{2,5}$/i, '')
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          if (normalized.length < 3 || !/[a-z\u00c0-\u024f]{2}/i.test(normalized)) return '';
          if (/^(?:img|image|photo|picture|frame|scan|dscf?|pxl|untitled|capture|screenshot)\s*\d+$/i.test(normalized)) return '';
          return normalized;
        };
        const candidates = archiveAlbums.flatMap((album) => [
          album.title,
          ...(album.media || []).map((item) => item.name),
        ]).map(usefulExample).filter(Boolean);
        const liveExamples = candidates.filter((value, index) =>
          candidates.findIndex((candidate) => candidate.toLowerCase() === value.toLowerCase()) === index
        ).slice(0, 3);
        search.placeholder = liveExamples.length
          ? `Search “${liveExamples.join('”, “')}”…`
          : 'Search albums…';
      }
    }

    document.querySelectorAll('[data-cms]').forEach((node) => {
      const value = contentAt(content, node.dataset.cms);
      if (typeof value === 'string') node.textContent = value;
    });
    document.querySelectorAll('[data-cms-lines]').forEach((node) => {
      const path = node.dataset.cmsLines;
      const value = contentAt(content, path);
      if (typeof value !== 'string') return;
      const parts = path.includes('Signal')
        ? value.split(/\s*\/\s*/)
        : value.split(/(?<=[.!?])\s+/);
      node.textContent = parts.filter(Boolean).join('\n');
      node.style.whiteSpace = 'pre-line';
    });
    if (content.menu) {
      const accountLabel = content.menu.accountLabel || 'My Library';
      document.querySelectorAll('[data-menu-account-label]').forEach((node) => {
        node.textContent = /account/i.test(accountLabel) ? 'My Library' : accountLabel;
      });
      ['instagram', 'vimeo', 'arena'].forEach((network) => {
        const link = document.querySelector(`[data-menu-social="${network}"]`);
        if (!link) return;
        const label = content.menu[`${network}Label`];
        const url = content.menu[`${network}Url`];
        if (label) link.textContent = `${label} ↗`;
        link.href = url || '#';
        if (url && url !== '#') {
          link.target = '_blank';
          link.rel = 'noreferrer';
        } else {
          link.removeAttribute('target');
          link.removeAttribute('rel');
        }
      });
    }
    applySectionVisibility(content.sections);
    const hatEnabled = content.sections?.hatDrop !== false;
    body.classList.toggle('hat-section-disabled', !hatEnabled);

    const hatCta = document.querySelector('[data-hat-cta]');
    if (hatCta) {
      const enabled = Boolean(content.hat?.ctaEnabled && content.hat?.ctaUrl);
      hatCta.classList.toggle('is-disabled', !enabled);
      hatCta.setAttribute('aria-disabled', String(!enabled));
      hatCta.querySelector('b').textContent = enabled ? '↗' : '—';
      hatCta.href = enabled ? content.hat.ctaUrl : '#hat-drop';
    }

    const studioTitle = [...document.querySelectorAll('.studio-title > span')];
    content.hero.studioTitle.forEach((line, index) => {
      if (!studioTitle[index]) return;
      if (index === studioTitle.length - 1) {
        const dot = studioTitle[index].querySelector('.studio-title__dot');
        studioTitle[index].textContent = line;
        if (dot) studioTitle[index].append(dot);
      } else {
        studioTitle[index].textContent = line;
      }
    });

    content.projects.forEach((project, index) => {
      const card = document.querySelector(`#${project.id}`);
      if (!card) return;
      const carousel = card.querySelector('[data-carousel]');
      const trackNode = card.querySelector('[data-carousel-track]');
      const selectedAlbum = archiveAlbums.find((album) =>
        album.id === project.albumId || album.slug === project.albumKey
      );
      const uploaded = media[project.id] || [];
      const fallbackMedia = uploaded.length
        ? uploaded.map((item) => ({
            url: item.url,
            name: item.alt || `${project.title} frame`,
            kind: item.kind || 'image',
          }))
        : project.images.map((url) => ({ url, name: `${project.title} frame`, kind: 'image' }));
      const projectMedia = selectedAlbum?.media?.length ? selectedAlbum.media : fallbackMedia;
      const displayTitle = selectedAlbum?.title || project.title;
      carousel.dataset.carouselName = displayTitle;
      trackNode.replaceChildren(...projectMedia.map((item, imageIndex) =>
        createProjectSlide(item, displayTitle, imageIndex, carousel)
      ));
      const meta = card.querySelector('.project-card__meta');
      const metaLines = meta.querySelectorAll(':scope > p');
      if (metaLines[0]) metaLines[0].textContent = project.category;
      if (metaLines[1]) metaLines[1].textContent = project.location;
      meta.querySelector('h3').textContent = displayTitle;
      const openButton = meta.querySelector('[data-open-album]');
      const openTarget = selectedAlbum?.id || '';
      openButton.textContent = project.cta;
      openButton.dataset.openAlbum = openTarget;
      openButton.hidden = !openTarget;
      const carouselNav = carousel.querySelector('.project-carousel__nav');
      carouselNav.setAttribute('role', 'group');
      carouselNav.setAttribute('aria-label', `${displayTitle} carousel controls`);
      carousel.querySelector('[data-carousel-prev]').setAttribute('aria-label', `Previous ${displayTitle} frame`);
      carousel.querySelector('[data-carousel-next]').setAttribute('aria-label', `Next ${displayTitle} frame`);
      carousel.querySelector('.project-carousel__readout i').textContent = `/ ${String(projectMedia.length).padStart(2, '0')}`;

      const rail = document.querySelectorAll('[data-project-jump]')[index];
      const railCover = projectMedia.find((item) => item.kind !== 'video');
      if (rail) {
        rail.setAttribute('aria-label', `Go to ${displayTitle || `project ${index + 1}`}`);
        const railImage = rail.querySelector('img');
        if (railCover?.url) {
          railImage.src = railCover.url;
          trackMediaLoad(railImage);
        }
      }
    });

    const portrait = document.querySelector('.about__portrait img');
    const uploadedPortrait = media['about-portrait']?.[0];
    const portraitSource = uploadedPortrait?.url || content.about.portraitUrl;
    portrait.src = portraitSource || placeholderImage(4 / 5);
    portrait.alt = portraitSource
      ? (uploadedPortrait?.alt || content.about.portraitAlt || 'Portrait of Nero')
      : '';
    trackMediaLoad(portrait);
    document.querySelector('.modalities').replaceChildren(
      ...content.about.modalities.map((label) => {
        const span = document.createElement('span');
        span.textContent = label;
        return span;
      }),
    );

    document.querySelectorAll('.service-list article').forEach((article, index) => {
      const service = content.studio.services[index];
      if (!service) return;
      article.querySelector('h3').textContent = service.title;
      article.querySelector('p').textContent = service.description;
      article.querySelector('small').textContent = service.meta;
    });
    const watchExampleMedia = (root) => root.querySelectorAll('img, video').forEach(trackMediaLoad);
    const examplesRail = document.querySelector('[data-studio-examples]');
    if (examplesRail) {
      // Every field here is owner/admin-editable copy coming back from
      // /api/site-content, and the server stores it verbatim. It must be escaped
      // before it reaches innerHTML, otherwise an admin holding only the
      // `website` permission could run script for every visitor.
      examplesRail.innerHTML = (content.studio.examples || []).map((example, index) => {
        const linkedAlbum = archiveAlbums.find((album) => album.id === example.albumId);
        const cover = linkedAlbum?.media?.find((item) => item.kind !== 'video') || linkedAlbum?.media?.[0];
        const href = linkedAlbum ? `/album?id=${encodeURIComponent(linkedAlbum.id)}` : '#contact';
        const mediaMarkup = cover?.url
          ? (cover.kind === 'video'
            ? `<video src="${escapeHTML(cover.url)}" muted playsinline preload="metadata" aria-hidden="true"></video>`
            : `<img src="${escapeHTML(cover.url)}" alt="" loading="lazy" decoding="async" />`)
          : `<img src="${placeholderImage(3 / 4)}" alt="" width="900" height="1200" />`;
        return `<a class="studio-example" href="${escapeHTML(href)}" data-example-index="${index}">
          <span class="studio-example__index">${String(index + 1).padStart(2, '0')}</span>
          <span class="studio-example__media" data-media-frame>${mediaMarkup}</span>
          <span class="studio-example__copy"><small>${escapeHTML(example.category)}</small><strong>${escapeHTML(example.title)}</strong><p>${escapeHTML(example.result)}</p><b>${linkedAlbum ? 'Open signal ↗' : 'Start a conversation ↗'}</b></span>
        </a>`;
      }).join('');
      watchExampleMedia(examplesRail);
      let dragging = false, startX = 0, startScroll = 0;
      examplesRail.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true; startX = event.clientX; startScroll = examplesRail.scrollLeft;
        examplesRail.setPointerCapture(event.pointerId); examplesRail.classList.add('is-dragging');
      });
      examplesRail.addEventListener('pointermove', (event) => { if (dragging) examplesRail.scrollLeft = startScroll - (event.clientX - startX); });
      examplesRail.addEventListener('pointerup', () => { dragging = false; examplesRail.classList.remove('is-dragging'); });
    }
    document.querySelectorAll('.proof__metrics article').forEach((article, index) => {
      const metric = content.studio.metrics[index];
      if (!metric) return;
      article.querySelector('strong').textContent = metric.value;
      article.querySelector('span').textContent = metric.label;
    });
    document.querySelectorAll('.process article').forEach((article, index) => {
      const step = content.studio.process[index];
      if (!step) return;
      article.querySelector('h3').textContent = step.title;
      article.querySelector('p').textContent = step.description;
    });

    const email = document.querySelector('[data-cms-email]');
    contactEmail = content.contact.email || contactEmail;
    email.href = `mailto:${contactEmail}`;
    email.firstChild.textContent = `${contactEmail} `;
    document.querySelectorAll('[data-contact-email-link]').forEach((link) => {
      link.href = `mailto:${contactEmail}`;
      link.textContent = contactEmail;
    });
    const mediaKitButton = document.querySelector('[data-media-kit]');
    mediaKitButton.dataset.downloadUrl = files['media-kit']?.url || '';
    mediaKitButton.hidden = false;
    mediaKitButton.classList.toggle('is-unavailable', !files['media-kit']);
    mediaKitButton.setAttribute('aria-disabled', String(!files['media-kit']));
    const mediaKitAction = mediaKitButton.querySelector('strong');
    if (mediaKitAction) mediaKitAction.textContent = files['media-kit'] ? 'DOWNLOAD ↓' : 'MEDIA KIT UPDATING';
  } catch (_) {
    // The static HTML remains a complete fallback if the publishing API is unavailable.
  }
}

await hydrateSiteContent();
document.querySelector('[data-back-to-top]')?.addEventListener('click', () => {
  window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' });
  track('footer_back_to_top');
});

const workSection = document.querySelector('#work');
const projectRail = document.querySelector('.project-rail');
const projectButtons = [...document.querySelectorAll('[data-project-jump]')];
const projectCurrent = document.querySelector('#project-current');
const workObserver = new IntersectionObserver(([entry]) => projectRail.classList.toggle('is-visible', entry.isIntersecting), { threshold: .04 });
workObserver.observe(workSection);
const projectObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const index = Number(entry.target.dataset.project);
    projectButtons.forEach((button, buttonIndex) => button.classList.toggle('is-active', buttonIndex === index));
    projectCurrent.textContent = String(index + 1).padStart(2, '0');
  });
}, { rootMargin: '-35% 0px -35% 0px' });
document.querySelectorAll('[data-project]').forEach((project) => projectObserver.observe(project));
projectButtons.forEach((button) => button.addEventListener('click', () => document.querySelector(`#${button.dataset.projectJump}`)?.scrollIntoView({ behavior: 'smooth' })));

// Each project frame is a swipeable filmstrip. Its active image also feeds the blurred optical halo.
const carouselControlRevealers = [];
document.querySelectorAll('[data-carousel]').forEach((carousel) => {
  const slides = [...carousel.querySelectorAll('[data-carousel-slide]')];
  const viewport = carousel.querySelector('[data-carousel-viewport]');
  const trackNode = carousel.querySelector('[data-carousel-track]');
  const current = carousel.querySelector('[data-carousel-current]');
  const halos = [...carousel.querySelectorAll('.project-carousel__halo')];
  const project = carousel.closest('[data-project]');
  const name = carousel.dataset.carouselName;

  // Published album content can legitimately be empty while an owner is still
  // assembling it. Leave the static project shell intact instead of wiring a
  // carousel that has no frame to display.
  if (!slides.length || !viewport || !trackNode || !current) {
    carousel.classList.add('is-empty');
    return;
  }

  let index = 0;
  let activeHaloIndex = 0;
  let pointerStart = null;
  let controlsTimer = 0;

  function revealControls(duration = 1800) {
    window.clearTimeout(controlsTimer);
    carousel.classList.add('is-controls-visible');
    controlsTimer = window.setTimeout(() => carousel.classList.remove('is-controls-visible'), duration);
  }

  function resetDragVisual() {
    viewport.style.setProperty('--drag-x', '0px');
    viewport.style.setProperty('--drag-y', '0px');
    viewport.style.setProperty('--drag-turn', '0deg');
  }
  carouselControlRevealers.push(() => {
    const bounds = carousel.getBoundingClientRect();
    if (bounds.bottom > 0 && bounds.top < innerHeight) revealControls(1250);
  });

  function showFrame(nextIndex, direction = 1, record = true) {
    index = (nextIndex + slides.length) % slides.length;
    carousel.dataset.direction = direction > 0 ? 'next' : 'previous';
    carousel.style.setProperty('--carousel-progress', String((index + 1) / slides.length));
    trackNode.style.transform = `translate3d(${-index * 100}%,0,0)`;
    current.textContent = String(index + 1).padStart(2, '0');
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      if (!active && slide.tagName === 'VIDEO') slide.pause();
    });
    const activeSlide = slides[index];
    const ratio = Number(activeSlide.dataset.aspectRatio);
    if (ratio) carousel.style.aspectRatio = String(ratio);
    const source = activeSlide.tagName === 'IMG' ? (activeSlide.currentSrc || activeSlide.src) : '';
    const escapedSource = source.replace(/"/g, '\\"');
    if (!source) {
      halos.forEach((halo) => {
        halo.classList.remove('is-active');
        halo.style.backgroundImage = '';
      });
    } else if (!record) {
      halos[activeHaloIndex].style.backgroundImage = `url("${escapedSource}")`;
    } else {
      const nextHaloIndex = activeHaloIndex === 0 ? 1 : 0;
      halos[nextHaloIndex].style.backgroundImage = `url("${escapedSource}")`;
      void halos[nextHaloIndex].offsetWidth;
      halos[nextHaloIndex].classList.add('is-active');
      halos[activeHaloIndex].classList.remove('is-active');
      activeHaloIndex = nextHaloIndex;
    }
    viewport.setAttribute('aria-label', `${name} frame ${index + 1} of ${slides.length}. Use arrow keys, drag, or tap to change frame.`);
    if (record) {
      revealControls();
      track('project_carousel_change', { project: project?.id, frame: index + 1, direction });
    }
  }

  carousel.querySelector('[data-carousel-prev]').addEventListener('click', () => showFrame(index - 1, -1));
  carousel.querySelector('[data-carousel-next]').addEventListener('click', () => showFrame(index + 1, 1));
  viewport.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); showFrame(index - 1, -1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); showFrame(index + 1, 1); }
  });
  viewport.addEventListener('pointerdown', (event) => {
    pointerStart = { x: event.clientX, y: event.clientY, type: event.pointerType };
    viewport.setPointerCapture(event.pointerId);
    carousel.classList.add('is-dragging');
    revealControls(2600);
  });
  viewport.addEventListener('pointermove', (event) => {
    if (pointerStart === null) return;
    const distanceX = event.clientX - pointerStart.x;
    const distanceY = event.clientY - pointerStart.y;
    trackNode.style.transform = `translate3d(calc(${-index * 100}% + ${distanceX}px),0,0)`;
    if (pointerStart.type === 'mouse' && innerWidth > 900) {
      viewport.style.setProperty('--drag-x', `${Math.max(-18, Math.min(18, distanceX * .08))}px`);
      viewport.style.setProperty('--drag-y', `${Math.max(-14, Math.min(14, distanceY * .08))}px`);
      viewport.style.setProperty('--drag-turn', `${Math.max(-1.1, Math.min(1.1, distanceX / 150))}deg`);
    }
  });
  viewport.addEventListener('pointerup', (event) => {
    if (pointerStart === null) return;
    const distance = event.clientX - pointerStart.x;
    carousel.classList.remove('is-dragging');
    pointerStart = null;
    resetDragVisual();
    // A deliberate swipe changes frame. A plain tap used to advance too, which
    // meant touching the image did something unexpected and gave no way back.
    // Taps now just settle the track and surface the controls.
    if (Math.abs(distance) > 32) {
      showFrame(index + (distance < 0 ? 1 : -1), distance < 0 ? 1 : -1);
    } else {
      trackNode.style.transform = `translate3d(${-index * 100}%,0,0)`;
      revealControls(3200);
    }
  });
  viewport.addEventListener('pointercancel', () => {
    pointerStart = null;
    carousel.classList.remove('is-dragging');
    resetDragVisual();
    trackNode.style.transform = `translate3d(${-index * 100}%,0,0)`;
  });
  showFrame(0, 1, false);
});
let carouselScrollFrame = 0;
window.addEventListener('scroll', () => {
  if (carouselScrollFrame) return;
  carouselScrollFrame = requestAnimationFrame(() => {
    carouselControlRevealers.forEach((reveal) => reveal());
    carouselScrollFrame = 0;
  });
}, { passive: true });

const portal = document.querySelector('#portal');
const portalContent = document.querySelector('#portal-content');
const accountState = {
  me: null,
  albums: [],
  pendingAlbum: null,
  pendingInvite: new URLSearchParams(location.search).get('albumInvite'),
  googleConfig: null
};
let portalCloseTimer = 0;
let portalCloseHandler = null;

async function apiRequest(path, options = {}) {
  const response = await fetch(path, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`);
  return data;
}

function openPortal(html, eventName = '') {
  if (portalCloseTimer) window.clearTimeout(portalCloseTimer);
  if (portalCloseHandler) portal.removeEventListener('animationend', portalCloseHandler);
  portalCloseTimer = 0;
  portalCloseHandler = null;
  portal.classList.remove('is-closing');
  portalContent.innerHTML = html;
  if (!portal.open) portal.showModal();
  portal.append(cursorDot, cursorRing);
  body.classList.add('is-locked');
  if (eventName) track(eventName);
}
function setPortalOrigin(source) {
  if (!source) return;
  const bounds = source.getBoundingClientRect();
  portal.style.setProperty('--portal-origin-x', `${bounds.left + bounds.width / 2}px`);
  portal.style.setProperty('--portal-origin-y', `${bounds.top + bounds.height / 2}px`);
}
function restorePortfolioCursor() {
  if (cursorDot.parentElement !== body) body.append(cursorDot, cursorRing);
}
function finishPortalClose() {
  if (portalCloseTimer) window.clearTimeout(portalCloseTimer);
  if (portalCloseHandler) portal.removeEventListener('animationend', portalCloseHandler);
  portalCloseTimer = 0;
  portalCloseHandler = null;
  portal.classList.remove('is-closing');
  if (portal.open) portal.close();
  restorePortfolioCursor();
  body.classList.remove('uses-native-pointer');
  body.classList.remove('is-locked');
}
function closePortal() {
  if (!portal.open || portal.classList.contains('is-closing')) return;
  if (reducedMotion) {
    finishPortalClose();
    return;
  }
  portal.classList.add('is-closing');
  portalCloseHandler = (event) => {
    if (event.target === portal && event.animationName === 'portal-dismiss') finishPortalClose();
  };
  portal.addEventListener('animationend', portalCloseHandler);
  portalCloseTimer = window.setTimeout(finishPortalClose, 760);
}
document.querySelector('[data-close-portal]').addEventListener('click', closePortal);
portal.addEventListener('click', (event) => { if (event.target === portal) closePortal(); });
portal.addEventListener('cancel', (event) => {
  event.preventDefault();
  closePortal();
});
portal.addEventListener('close', finishPortalClose);
function portalLoading(label = 'Opening your library') {
  openPortal(`
    <div class="portal__loading" role="status">
      <span class="portal__loader" aria-hidden="true"></span>
      <p class="kicker">NERO ID / SECURE CHANNEL</p>
      <h2>${escapeHTML(label)}<span>.</span></h2>
    </div>
  `);
}

function loadGoogleClient() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loadGoogleClient.promise) return loadGoogleClient.promise;
  loadGoogleClient.promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Google sign-in could not load.'));
    document.head.append(script);
  });
  return loadGoogleClient.promise;
}

async function mountGoogleButton() {
  const mount = portalContent.querySelector('#google-signin');
  if (!mount) return;
  mount.addEventListener('pointerenter', () => body.classList.add('uses-native-pointer'));
  mount.addEventListener('pointerleave', () => body.classList.remove('uses-native-pointer'));
  try {
    accountState.googleConfig ||= await apiRequest('/api/auth/config');
    if (!accountState.googleConfig.googleClientId) {
      mount.innerHTML = '<p class="portal__system-note">Google sign-in is ready in the build and will activate when the OAuth client ID is connected.</p>';
      return;
    }
    await loadGoogleClient();
    window.google.accounts.id.initialize({
      client_id: accountState.googleConfig.googleClientId,
      callback: async ({ credential }) => {
        mount.classList.add('is-busy');
        try {
          await apiRequest('/api/auth/google', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credential })
          });
          track('google_sign_in');
          if (accountState.pendingInvite) return await redeemPendingInvite();
          await renderAccount();
        } catch (error) {
          mount.classList.remove('is-busy');
          showPortalError(error.message);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true
    });
    window.google.accounts.id.renderButton(mount, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: 'continue_with',
      logo_alignment: 'left',
      width: Math.min(480, Math.max(240, Math.floor(mount.getBoundingClientRect().width)))
    });
  } catch (error) {
    mount.innerHTML = `<p class="portal__system-note">${escapeHTML(error.message)}</p>`;
  }
}

function showPortalError(message) {
  let node = portalContent.querySelector('.portal__error');
  if (!node) {
    node = document.createElement('p');
    node.className = 'portal__error';
    node.setAttribute('role', 'alert');
    portalContent.prepend(node);
  }
  node.textContent = message;
}

function updateAccountLabels() {
  document.querySelectorAll('[data-menu-account-label]').forEach((label) => {
    label.textContent = 'My Library';
  });
}

function renderPreviewIdentity() {
  return `
    <form class="portal__preview-form" data-portal-form="preview-login">
      <span>LOCAL PREVIEW IDENTITY</span>
      <input name="name" autocomplete="name" required placeholder="YOUR NAME" />
      <input name="email" type="email" autocomplete="email" required placeholder="YOUR GOOGLE EMAIL" />
      <button type="submit">Preview this account →</button>
    </form>
  `;
}

async function renderAnonymous(note = '') {
  const me = accountState.me;
  openPortal(`
    <div class="portal__auth">
      <div class="portal__auth-copy">
        <p class="kicker">NERO ID / PRIVATE ACCESS</p>
        <h2>Your archive.<br><em>One sign-in.</em></h2>
        <p>${note ? escapeHTML(note) : 'Sign in to open your private library and keep future album access in one place.'}</p>
        <div class="portal__features"><span>Private access</span><span>Original delivery</span><span>Saved library</span></div>
      </div>
      <div class="portal__auth-card">
        <span class="portal__auth-index">SIGNAL / AUTH 01</span>
        <h3>Continue to the archive</h3>
        <div id="google-signin" class="portal__google"></div>
        ${me?.localPreview ? renderPreviewIdentity() : ''}
        <small>Google verifies who you are. NERO decides which albums you can see.</small>
      </div>
    </div>
  `, 'account_modal');
  await mountGoogleButton();
}

function renderBootstrap(me) {
  openPortal(`
    <div class="portal__auth">
      <div class="portal__auth-copy">
        <p class="kicker">FIRST RUN / OWNER CLAIM</p>
        <h2>Make this<br><em>your control room.</em></h2>
        <p>You are signed in as <strong>${escapeHTML(me.identity.email)}</strong>. Claim the first owner profile, then create client accounts and decide exactly who sees each album.</p>
      </div>
      <form class="portal__auth-card" data-portal-form="bootstrap">
        <span class="portal__auth-index">NERO / OWNER 001</span>
        <label>Display name<input name="name" required value="${escapeHTML(me.identity.name)}" /></label>
        <button type="submit">Claim owner access →</button>
        <small>This first-owner action is available once, before public access is enabled.</small>
      </form>
    </div>
  `);
}

function renderNotInvited(me, note = '') {
  openPortal(`
    <div class="portal__auth">
      <div class="portal__auth-copy">
        <p class="kicker">IDENTITY VERIFIED / ACCESS PENDING</p>
        <h2>Right person.<br><em>No album yet.</em></h2>
        <p>${note ? escapeHTML(note) : 'The studio has not assigned an album to this email yet.'}</p>
      </div>
      <div class="portal__auth-card portal__identity-card">
        <span class="portal__auth-index">SIGNED IN WITH GOOGLE</span>
        <strong>${escapeHTML(me.identity.name)}</strong>
        <p>${escapeHTML(me.identity.email)}</p>
        <button type="button" data-account-logout>Use another account</button>
        <a href="mailto:${escapeHTML(contactEmail)}?subject=Album%20access%20request" data-contact-email-request>Request access ↗</a>
      </div>
    </div>
  `);
}

function albumCardTemplate(album) {
  const cover = album.images?.[0] || '';
  const mediaCount = album.media?.length ?? album.images.length;
  const state = album.visibility === 'public' ? 'PUBLIC' : 'ASSIGNED';
  return `
    <a class="portal-album" href="/album?id=${encodeURIComponent(album.id)}" data-portal-album="${escapeHTML(album.id)}">
      <span class="portal-album__image">
        ${cover
          ? `<img src="${escapeHTML(cover)}" alt="" loading="lazy" decoding="async" />`
          : `<img src="${placeholderImage()}" alt="" width="1200" height="800" />`}
        <b>${state}</b>
      </span>
      <span class="portal-album__meta">
        <small>${escapeHTML(album.status)} / ${mediaCount} FILE${mediaCount === 1 ? '' : 'S'}</small>
        <strong>${escapeHTML(album.title)}</strong>
        <i>OPEN ALBUM ↗</i>
      </span>
    </a>
  `;
}

async function renderLibrary(me) {
  const { albums } = await apiRequest('/api/albums');
  accountState.albums = albums;
  const privateCount = albums.filter((album) => album.visibility === 'private').length;
  openPortal(`
    <div class="portal-library">
      <header class="portal-library__header">
        <div>
          <p class="kicker">NERO ID / ${escapeHTML(me.user.role.toUpperCase())}</p>
          <h2>Welcome back,<br><em>${escapeHTML(me.user.name.split(' ')[0])}.</em></h2>
        </div>
        <div class="portal-library__identity">
          <div class="portal-library__identity-meta">
            <span>${escapeHTML(me.user.email)}</span>
            <small>${privateCount} PRIVATE ALBUM${privateCount === 1 ? '' : 'S'} ASSIGNED</small>
          </div>
          <div class="portal-library__actions">
            <button class="portal-library__action portal-library__action--quiet" type="button" data-account-logout>
              <span>Sign out</span><b aria-hidden="true">↙</b>
            </button>
            ${['owner', 'admin'].includes(me.user.role) ? `
              <a class="portal-library__action portal-library__action--admin" href="/studio">
                <span>Open back office</span><b aria-hidden="true">↗</b>
              </a>
            ` : ''}
          </div>
        </div>
      </header>
      <div class="portal-library__grid">
        ${albums.length ? albums.map(albumCardTemplate).join('') : `
          <div class="portal-library__empty">
            <p class="kicker">NO ACTIVE DELIVERIES</p>
            <h3>Your next album will appear here.</h3>
            <p>The studio controls access by your verified Google email.</p>
          </div>
        `}
      </div>
    </div>
  `);
}

async function renderAccount(note = '') {
  portalLoading();
  try {
    const me = await apiRequest('/api/me');
    accountState.me = me;
    updateAccountLabels();
    if (me.bootstrapRequired && me.identity && !me.user) return renderBootstrap(me);
    if (!me.identity) return renderAnonymous(note);
    if (!me.user) return renderNotInvited(me, note);
    return await renderLibrary(me);
  } catch (error) {
    openPortal(`
      <div class="portal__failure">
        <p class="kicker">SIGNAL INTERRUPTED</p>
        <h2>The archive did not answer.</h2>
        <p>${escapeHTML(error.message)}</p>
        <button type="button" data-account-retry>Try again →</button>
      </div>
    `);
  }
}

async function redeemPendingInvite() {
  if (!accountState.pendingInvite) return renderAccount();
  portalLoading('Saving the album');
  try {
    const { album } = await apiRequest(`/api/album-invites/${encodeURIComponent(accountState.pendingInvite)}`, {
      method: 'POST'
    });
    accountState.pendingAlbum = album.id;
    showToast(`${album.title} is now saved to your library.`);
    track('album_invite_redeemed', { albumId: album.id });
    return renderAlbum(album.id);
  } catch (error) {
    if (/sign in/i.test(error.message)) {
      return renderAccount('Sign in with Google to save this shared album to your library.');
    }
    return renderAccount(error.message);
  }
}

async function openPendingInvite() {
  if (!accountState.pendingInvite) return;
  setPortalOrigin(document.querySelector('[data-open-account]'));
  portalLoading('Reading the shared link');
  try {
    const { invite } = await apiRequest(`/api/album-invites/${encodeURIComponent(accountState.pendingInvite)}`);
    if (invite.authenticated) return redeemPendingInvite();
    return renderAccount(`Sign in with Google to save “${invite.albumTitle}” to your library.`);
  } catch (error) {
    return renderAccount(error.message);
  }
}

async function renderAlbum(key) {
  track('album_open', { albumId: key });
  location.assign(`/album?id=${encodeURIComponent(key)}`);
}

document.querySelectorAll('[data-open-account]').forEach((button) => button.addEventListener('click', (event) => {
  setPortalOrigin(event.currentTarget);
  toggleMenu(false);
  accountState.pendingAlbum = null;
  renderAccount();
}));
document.querySelectorAll('[data-open-locked]').forEach((button) => button.addEventListener('click', async (event) => {
  setPortalOrigin(event.currentTarget);
  accountState.pendingAlbum = button.dataset.openLocked;
  track('locked_album_open', { albumId: accountState.pendingAlbum });
  await renderAlbum(accountState.pendingAlbum);
}));
document.querySelectorAll('[data-open-album]').forEach((button) => button.addEventListener('click', async (event) => {
  setPortalOrigin(event.currentTarget);
  track('public_album_open', { albumId: button.dataset.openAlbum });
  await renderAlbum(button.dataset.openAlbum);
}));

portal.addEventListener('submit', async (event) => {
  event.preventDefault();
  const kind = event.target.dataset.portalForm;
  const button = event.target.querySelector('[type="submit"]');
  button?.setAttribute('disabled', '');
  try {
    if (kind === 'preview-login') {
      const form = new FormData(event.target);
      await apiRequest('/api/dev/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.get('name'), email: form.get('email') })
      });
      if (accountState.pendingInvite) return await redeemPendingInvite();
      return await renderAccount();
    }
    if (kind === 'bootstrap') {
      const form = new FormData(event.target);
      await apiRequest('/api/auth/bootstrap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.get('name') })
      });
      track('owner_bootstrap');
      return await renderAccount();
    }
  } catch (error) {
    showPortalError(error.message);
    button?.removeAttribute('disabled');
  }
});

portal.addEventListener('click', async (event) => {
  const target = event.target.closest('button, a');
  if (!target) return;
  if (target.matches('[data-account-retry]')) return renderAccount();
  if (target.matches('[data-album-back]')) return renderAccount();
  if (target.matches('[data-portal-album]')) return renderAlbum(target.dataset.portalAlbum);
  if (target.matches('[data-account-logout]')) {
    target.setAttribute('disabled', '');
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
      if (accountState.me?.localPreview) {
        await apiRequest('/api/dev/session', { method: 'DELETE' });
      }
      window.google?.accounts?.id?.disableAutoSelect?.();
      accountState.me = null;
      accountState.albums = [];
      return renderAccount();
    } catch (error) {
      showPortalError(error.message);
      target.removeAttribute('disabled');
    }
  }
  if (target.matches('[data-portal-share]')) {
    await shareAlbum(target.dataset.portalShareId, target.dataset.portalShare);
  }
});

const toast = document.querySelector('#toast');
let toastTimer;
function showToast(message) {
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4200);
}

// One implementation for every share affordance, always pointing at the album's
// own shareable URL rather than the current page.
async function shareAlbum(albumId, title = '') {
  if (!albumId) return;
  const shareUrl = new URL('/share/album', location.origin);
  shareUrl.searchParams.set('id', albumId);
  const label = title || 'Album';
  try {
    if (navigator.share) {
      await navigator.share({ title: label, text: `View ${label} by NERO`, url: shareUrl.href });
    } else {
      await navigator.clipboard.writeText(shareUrl.href);
      showToast('Album link copied.');
    }
    track('album_share', { albumId });
  } catch (_) { /* Share sheet cancelled. */ }
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-share]');
  if (!button) return;
  shareAlbum(button.dataset.share, button.dataset.shareTitle);
});

const archiveSearch = document.querySelector('#archive-search');
function filterArchive({ report = false } = {}) {
  const query = archiveSearch.value.trim().toLowerCase();
  let visible = 0;
  document.querySelectorAll('.album-card').forEach((card) => {
    const keywords = card.dataset.keywords || '';
    const matches = !query || query.split(/\s+/).some((word) => keywords.includes(word));
    const show = matches && visible < archivePreviewLimit();
    card.hidden = !show;
    if (show) visible += 1;
  });
  document.querySelector('#vault-empty').hidden = visible > 0;
  if (report) reportArchiveSearch(query, visible);
}
const reportArchiveSearch = debounce((query, results) => track('archive_search', { query, results }), 600);
document.querySelector('#search-button').addEventListener('click', () => filterArchive({ report: true }));
archiveSearch.addEventListener('keydown', (event) => { if (event.key === 'Enter') filterArchive({ report: true }); });
archiveSearch.addEventListener('input', () => filterArchive({ report: true }));
window.addEventListener('resize', debounce(() => filterArchive(), 200), { passive: true });

document.addEventListener('click', async (event) => {
  const favorite = event.target.closest('[data-favorite-album]');
  if (!favorite) return;
  event.preventDefault();
  event.stopPropagation();
  const album = archiveAlbums.find((item) => item.id === favorite.dataset.favoriteAlbum);
  if (!album) return;
  const next = !album.favorite;
  try {
    const response = await fetch(`/api/albums/${encodeURIComponent(album.id)}/favorite`, { method: next ? 'PUT' : 'DELETE' });
    if (response.status === 401) {
      accountState.note = 'Sign in to keep a personal list of favourite albums.';
      openPortal(document.querySelector('[data-open-account]'));
      return;
    }
    if (!response.ok) throw new Error('Favourite could not be saved.');
    album.favorite = next;
    favorite.textContent = next ? '♥' : '♡';
    favorite.setAttribute('aria-pressed', String(next));
    showToast(next ? 'Saved to My Library.' : 'Removed from favourites.');
  } catch (error) { showToast(error.message); }
});

document.querySelector('[data-media-kit]').addEventListener('click', (event) => {
  const url = event.currentTarget.dataset.downloadUrl;
  if (!url) return showToast('The media kit is being updated. Check back shortly.');
  track('media_kit_download');
  window.location.assign(url);
});
// These forms used to swallow every submission and tell the visitor it had been
// "captured in prototype mode". There is no mail provider wired up, so instead
// of pretending, they compose the message in the visitor's own mail client —
// which actually reaches the inbox.
document.querySelector('[data-mail-form="enquiry"]')?.addEventListener('submit', async (event) => {
  event.preventDefault();
  const form = event.currentTarget;
  const data = new FormData(form);
  if (!form.checkValidity()) {
    form.querySelector(':invalid')?.focus();
    showToast('Add your name, email and a short message first.');
    return;
  }
  const button = form.querySelector('button[type="submit"]');
  const buttonLabel = button?.querySelector('span');
  const originalLabel = buttonLabel?.textContent;
  if (button) button.disabled = true;
  if (buttonLabel) buttonLabel.textContent = 'Sending…';
  try {
    const response = await fetch('/api/contact', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
      name: data.get('name'), email: data.get('email'), service: data.get('service'), message: data.get('message'),
      companyWebsite: data.get('companyWebsite'), startedAt: Number(form.dataset.startedAt || Date.now() - 2000),
    }) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || 'The message could not be sent.');
    showToast('Message sent. I’ll reply by email.');
    track('form_submit', { kind: 'enquiry' });
    form.reset();
    form.dataset.startedAt = String(Date.now());
  } catch (error) { showToast(error.message); }
  finally { if (button) button.disabled = false; if (buttonLabel) buttonLabel.textContent = originalLabel; }
});

const newsletterForm = document.querySelector('[data-mail-form="newsletter"]');
newsletterForm?.addEventListener('submit', (event) => {
  event.preventDefault();
  if (!newsletterForm.checkValidity()) return newsletterForm.querySelector(':invalid')?.focus();
  const email = String(new FormData(newsletterForm).get('email') || '').trim();
  window.location.href = mailtoLink(contactEmail, 'NERO / newsletter', `Please add this address to the NERO list: ${email}`);
  track('form_submit', { kind: 'newsletter' });
});

document.querySelector('[data-mail-form="enquiry"]')?.setAttribute('data-started-at', String(Date.now()));

registerServiceWorker();

const cursorDot = document.querySelector('.cursor--dot');
const cursorRing = document.querySelector('.cursor--ring');
const cursorInteractiveSelector = 'a, button, input, textarea, select, label, iframe';
let cursorX = 0, cursorY = 0;
let ringLeft = -17, ringTop = -17, ringWidth = 34, ringHeight = 34;
let cursorTarget = null;
const fineCursorPointer = matchMedia('(hover:hover) and (pointer:fine)');
if (!fineCursorPointer.matches) body.classList.add('is-touch');

function cursorCornerRadius(element, bounds, offset) {
  const styles = getComputedStyle(element);
  const cornerValues = [
    styles.borderTopLeftRadius,
    styles.borderTopRightRadius,
    styles.borderBottomRightRadius,
    styles.borderBottomLeftRadius
  ];
  const transparentBackground = styles.backgroundColor === 'rgba(0, 0, 0, 0)'
    || styles.backgroundColor === 'transparent';
  const containsMedia = Boolean(element.querySelector('img, picture, video, canvas'));
  const borderless = styles.borderStyle === 'none'
    || Number.parseFloat(styles.borderTopWidth) === 0;
  const textOnlyControl = element.matches('.text-button, .bare-link, .menu-trigger')
    || (element.matches('a, button, label')
      && transparentBackground && borderless && !containsMedia);
  const outerWidth = bounds.width + offset * 2;
  const outerHeight = bounds.height + offset * 2;
  const maximumRadius = Math.min(outerWidth, outerHeight) / 2;
  if (textOnlyControl) return `${maximumRadius}px`;

  const resolveRadius = (value) => {
    const firstRadius = value.trim().split(/\s+/)[0];
    const numericRadius = Number.parseFloat(firstRadius) || 0;
    const resolvedRadius = firstRadius.endsWith('%')
      ? Math.min(bounds.width, bounds.height) * numericRadius / 100
      : numericRadius;
    return `${Math.min(resolvedRadius + offset, maximumRadius)}px`;
  };
  return cornerValues.map(resolveRadius).join(' ');
}

function cursorGeometry() {
  if (cursorTarget?.isConnected) {
    const bounds = cursorTarget.getBoundingClientRect();
    if (bounds.width && bounds.height) {
      const offset = cursorTarget.matches('.menu-overlay__links > a, .menu-overlay__links > button') ? 12 : 4;
      return {
        left: bounds.left - offset,
        top: bounds.top - offset,
        width: bounds.width + offset * 2,
        height: bounds.height + offset * 2,
        radius: cursorCornerRadius(cursorTarget, bounds, offset)
      };
    }
  }
  return { left: cursorX - 17, top: cursorY - 17, width: 34, height: 34, radius: '17px' };
}

function positionCursor(ease = 1) {
  const target = cursorGeometry();
  ringLeft += (target.left - ringLeft) * ease;
  ringTop += (target.top - ringTop) * ease;
  ringWidth += (target.width - ringWidth) * ease;
  ringHeight += (target.height - ringHeight) * ease;
  cursorDot.style.transform = `translate(${cursorX - 2.5}px, ${cursorY - 2.5}px)`;
  cursorRing.style.width = `${ringWidth}px`;
  cursorRing.style.height = `${ringHeight}px`;
  cursorRing.style.borderRadius = target.radius;
  cursorRing.style.transform = `translate(${ringLeft}px, ${ringTop}px)`;
}

function refreshCursorTarget() {
  if (!fineCursorPointer.matches || !body.classList.contains('has-pointer')) return;
  const hitTarget = document.elementFromPoint(cursorX, cursorY);
  const nextTarget = hitTarget?.closest?.('[data-cursor-ignore]')
    ? null
    : hitTarget?.closest?.('[data-cursor-self]')
      || hitTarget?.closest?.('[data-cursor-frame]')
      || hitTarget?.closest?.(cursorInteractiveSelector)
      || null;
  if (nextTarget !== cursorTarget) {
    cursorTarget = nextTarget;
    cursorRing.classList.toggle('is-targeting', Boolean(cursorTarget));
  }
  if (reducedMotion) positionCursor(1);
}

window.addEventListener('pointermove', (event) => {
  if (event.pointerType !== 'mouse' || !fineCursorPointer.matches) {
    body.classList.add('is-touch');
    body.classList.remove('has-pointer');
    cursorTarget = null;
    cursorRing.classList.remove('is-targeting');
    return;
  }
  body.classList.remove('is-touch');
  cursorX = event.clientX;
  cursorY = event.clientY;
  body.classList.add('has-pointer');
  refreshCursorTarget();
}, { once: false });

window.addEventListener('scroll', refreshCursorTarget, { passive: true, capture: true });
portalContent.addEventListener('scroll', refreshCursorTarget, { passive: true });
window.addEventListener('resize', refreshCursorTarget, { passive: true });

function animateCursor() {
  positionCursor(cursorTarget ? .22 : .16);
  requestAnimationFrame(animateCursor);
}
if (fineCursorPointer.matches && !reducedMotion) animateCursor();

if (accountState.pendingInvite) openPendingInvite();
else if (pageParams.get('account') === '1') {
  setPortalOrigin(document.querySelector('[data-open-account]'));
  renderAccount(pageParams.get('note') || '');
}
track('page_view', { mode: body.dataset.mode, referrer: document.referrer || 'direct' });
